// src/components/module/compras/ComprasDataTable.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useContext, useState, useMemo } from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Tooltip,
  Chip,
  Button,
  Typography,
  Paper,
  Fade,
  Stack,
  Tab,
  Tabs,
  alpha,
  Skeleton,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  ShoppingCartCheckout as ShoppingCartCheckoutIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  TrendingUp,
  Receipt,
  Schedule,
  CheckCircleOutline,
  CancelOutlined,
  LocalShipping,
  FilterList,
} from "@mui/icons-material";
import { ICompra } from "../../types/compras.types";
import { format } from "date-fns";
import ComprasPDFButton from "./ComprasPDFButton";
import { DarkModeContext } from "../../../context/darkModeContext";

interface ComprasDataTableProps {
  items: ICompra[];
  onView: (compra: ICompra) => void;
  onAdd: () => void;
  onAddArticulos: (compra: ICompra) => void;
  onGestionarCompra: (compra: ICompra) => void;
  onPrint?: (compra: ICompra) => void;
  isLoading: boolean;
}

// ============================================================
// PALETA NOTION/LINEAR
// ============================================================
const PALETTE = {
  light: {
    bg: "#FFFFFF",
    bgSecondary: "#F7F7F5",
    bgTertiary: "#F1F1EF",
    text: "#37352F",
    textSecondary: "#6B6B6B",
    textTertiary: "#9B9A97",
    border: "#E9E9E7",
    borderHover: "#D3D3D0",
    accent: "#2383E2",
    accentLight: "#E8F0FE",
    success: "#0F7B6C",
    successBg: "#DBEDDB",
    warning: "#D9730D",
    warningBg: "#FADEC9",
    error: "#E03E3E",
    errorBg: "#FBE4E4",
    infoBg: "#E8F0FE",
    purple: "#6940A5",
    purpleBg: "#EAE4F2",
    kpiShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
    cardShadow: "0 1px 3px rgba(0,0,0,0.04)",
    hoverBg: "rgba(55, 53, 47, 0.04)",
  },
  dark: {
    bg: "#191919",
    bgSecondary: "#202020",
    bgTertiary: "#2B2B2B",
    text: "#E8E8E6",
    textSecondary: "#9B9A97",
    textTertiary: "#6B6B6B",
    border: "#333333",
    borderHover: "#444444",
    accent: "#529CCA",
    accentLight: "#143A52",
    success: "#4DAB9A",
    successBg: "#1A2F2A",
    warning: "#C77D48",
    warningBg: "#2E2215",
    error: "#D44C47",
    errorBg: "#3D2020",
    infoBg: "#1A2B3D",
    purple: "#9A6DD7",
    purpleBg: "#2A2040",
    kpiShadow: "0 1px 3px rgba(0,0,0,0.2)",
    cardShadow: "0 1px 3px rgba(0,0,0,0.2)",
    hoverBg: "rgba(255, 255, 255, 0.04)",
  },
};

// ============================================================
// ESTADO CONFIG
// ============================================================
const ESTADO_CONFIG: Record<string, {
  color: string;
  bg: string;
  icon: React.ReactElement;
  label: string;
}> = {
  abierta: { color: "accent", bg: "accentLight", icon: <Receipt sx={{ fontSize: 14 }} />, label: "Abierta" },
  pendiente: { color: "warning", bg: "warningBg", icon: <Schedule sx={{ fontSize: 14 }} />, label: "Pendiente" },
  parcial: { color: "purple", bg: "purpleBg", icon: <LocalShipping sx={{ fontSize: 14 }} />, label: "Parcial" },
  finalizada: { color: "success", bg: "successBg", icon: <CheckCircleOutline sx={{ fontSize: 14 }} />, label: "Finalizada" },
  cancelada: { color: "error", bg: "errorBg", icon: <CancelOutlined sx={{ fontSize: 14 }} />, label: "Cancelada" },
};

const getEstadoConfig = (estado: string) => {
  const key = estado.toLowerCase();
  for (const [k, v] of Object.entries(ESTADO_CONFIG)) {
    if (key.includes(k)) return v;
  }
  return ESTADO_CONFIG.abierta;
};

// ============================================================
// KPI CARD
// ============================================================
const KPICard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactElement;
  accent?: string;
  darkMode: boolean;
}> = ({ label, value, icon, accent, darkMode }) => {
  const p = darkMode ? PALETTE.dark : PALETTE.light;
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: "10px",
        border: `1px solid ${p.border}`,
        bgcolor: p.bg,
        boxShadow: p.kpiShadow,
        flex: 1,
        minWidth: 160,
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: p.borderHover,
          boxShadow: darkMode
            ? "0 2px 8px rgba(0,0,0,0.3)"
            : "0 2px 8px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: accent || p.bgTertiary,
            color: accent ? p.bg : p.textSecondary,
            "& svg": { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              color: p.textTertiary,
              letterSpacing: "0.02em",
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: "1.35rem",
              fontWeight: 700,
              color: p.text,
              lineHeight: 1.3,
              fontFeatureSettings: "'tnum'",
            }}
          >
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

// ============================================================
// ESTADO BADGE
// ============================================================
const EstadoBadge: React.FC<{ estado: string; darkMode: boolean }> = ({
  estado,
  darkMode,
}) => {
  const p = darkMode ? PALETTE.dark : PALETTE.light;
  const config = getEstadoConfig(estado);
  const bgColor = (p as any)[config.bg] || p.bgTertiary;
  const textColor = (p as any)[config.color] || p.text;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.5,
        py: 0.5,
        borderRadius: "6px",
        bgcolor: bgColor,
        color: textColor,
        fontSize: "0.78rem",
        fontWeight: 600,
        letterSpacing: "0.01em",
        lineHeight: 1.4,
        "& svg": { fontSize: 13, opacity: 0.85 },
      }}
    >
      {config.icon}
      {config.label}
    </Box>
  );
};

// ============================================================
// ACTION BUTTON
// ============================================================
const ActionBtn: React.FC<{
  tooltip: string;
  icon: React.ReactElement;
  onClick?: () => void;
  disabled?: boolean;
  darkMode: boolean;
  variant?: "default" | "accent" | "warning";
}> = ({ tooltip, icon, onClick, disabled = false, darkMode, variant = "default" }) => {
  const p = darkMode ? PALETTE.dark : PALETTE.light;
  const colors = {
    default: { bg: p.bgTertiary, hover: p.borderHover, text: p.textSecondary },
    accent: { bg: p.accentLight, hover: darkMode ? "#1C4466" : "#D4E4F7", text: p.accent },
    warning: { bg: p.warningBg, hover: darkMode ? "#3A2A18" : "#F5D0A9", text: p.warning },
  };
  const c = colors[variant];

  return (
    <Tooltip title={tooltip} arrow>
      <span>
        <IconButton
          onClick={onClick}
          disabled={disabled}
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            color: disabled ? p.textTertiary : c.text,
            bgcolor: disabled ? "transparent" : c.bg,
            opacity: disabled ? 0.4 : 1,
            transition: "all 0.15s ease",
            "& svg": { fontSize: 17 },
            "&:hover": {
              bgcolor: disabled ? "transparent" : c.hover,
            },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const ComprasDataTable: React.FC<ComprasDataTableProps> = ({
  items,
  onView,
  onAdd,
  onAddArticulos,
  onGestionarCompra,
  isLoading,
}) => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? PALETTE.dark : PALETTE.light;
  const [tabFilter, setTabFilter] = useState("todas");

  // ── KPIs ──
  const kpis = useMemo(() => {
    const total = items.length;
    const abiertas = items.filter((c) => c.estadoCompra?.Nombre?.toLowerCase().includes("abierta")).length;
    const pendientes = items.filter((c) => c.estadoCompra?.Nombre?.toLowerCase().includes("pendiente")).length;
    const finalizadas = items.filter((c) => c.estadoCompra?.Nombre?.toLowerCase().includes("finalizada")).length;
    const canceladas = items.filter((c) => c.estadoCompra?.Nombre?.toLowerCase().includes("cancelada")).length;
    return { total, abiertas, pendientes, finalizadas, canceladas };
  }, [items]);

  // ── Filtered items ──
  const filteredItems = useMemo(() => {
    if (tabFilter === "todas") return items;
    return items.filter((c) =>
      c.estadoCompra?.Nombre?.toLowerCase().includes(tabFilter)
    );
  }, [items, tabFilter]);

  // ── Tab counts ──
  const tabs = [
    { key: "todas", label: "Todas", count: kpis.total },
    { key: "abierta", label: "Abiertas", count: kpis.abiertas },
    { key: "pendiente", label: "Pendientes", count: kpis.pendientes },
    { key: "finalizada", label: "Finalizadas", count: kpis.finalizadas },
    { key: "cancelada", label: "Canceladas", count: kpis.canceladas },
  ];

  // ── Theme ──
  // ── Columns ──
  const columns: GridColDef[] = [
    {
      field: "OrdenCompra",
      headerName: "Orden",
      width: 110,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            color: p.accent,
            fontFamily: "'IBM Plex Mono', monospace",
            letterSpacing: "-0.01em",
          }}
        >
          OC-{params.value}
        </Typography>
      ),
    },
    {
      field: "FechaSolicitud",
      headerName: "Solicitud",
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return "—";
        const d = /^\d{4}-\d{2}-\d{2}$/.test(params.value) ? new Date(params.value + 'T12:00:00') : new Date(params.value);
        return format(d, "dd MMM yyyy");
      },
    },
    {
      field: "FechaEntrega",
      headerName: "Entrega",
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return "—";
        const d = /^\d{4}-\d{2}-\d{2}$/.test(params.value) ? new Date(params.value + 'T12:00:00') : new Date(params.value);
        return format(d, "dd MMM yyyy");
      },
    },
    {
      field: "proveedor",
      headerName: "Proveedor",
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params.row.proveedor?.Nombre || "—",
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              bgcolor: p.bgTertiary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: 700,
              color: p.textSecondary,
              flexShrink: 0,
            }}
          >
            {(params.row.proveedor?.Nombre || "?")[0]}
          </Box>
          <Typography
            sx={{
              fontSize: "0.84rem",
              fontWeight: 500,
              color: p.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.row.proveedor?.Nombre || "—"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "estadoCompra",
      headerName: "Estado",
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <EstadoBadge
          estado={params.row.estadoCompra?.Nombre || "Desconocido"}
          darkMode={darkMode}
        />
      ),
    },
    {
      field: "usuario",
      headerName: "Creado por",
      width: 140,
      valueGetter: (params) => params.row.usuario?.Usuario || "—",
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: "0.82rem",
            color: p.textSecondary,
            fontWeight: 400,
          }}
        >
          {params.row.usuario?.Usuario || "—"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const estado = params.row.estadoCompra?.Nombre || "";
        const isAbierta = estado.toLowerCase().includes("abierta");
        const enProceso = estado.toLowerCase().includes("pendiente");
        const puedeVer =
          estado.toLowerCase().includes("finalizada") ||
          estado.toLowerCase().includes("cancelada");

        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {puedeVer && (
              <ActionBtn
                tooltip="Ver detalles"
                icon={<VisibilityIcon />}
                onClick={() => onView(params.row)}
                darkMode={darkMode}
              />
            )}
            {puedeVer && <ComprasPDFButton compra={params.row} />}
            <ActionBtn
              tooltip={enProceso ? "Gestionar compra" : "Solo en estado Pendiente"}
              icon={<SettingsIcon />}
              onClick={() => onGestionarCompra(params.row)}
              disabled={!enProceso}
              darkMode={darkMode}
              variant="accent"
            />
            <ActionBtn
              tooltip={isAbierta ? "Añadir artículos" : "Solo órdenes abiertas"}
              icon={<ShoppingCartCheckoutIcon />}
              onClick={() => onAddArticulos(params.row)}
              disabled={!isAbierta}
              darkMode={darkMode}
              variant="warning"
            />
          </Stack>
        );
      },
    },
  ];

  return (
    <Fade in timeout={350}>
        <Box>
          {/* ── KPI CARDS ── */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mb: 3,
              flexWrap: "wrap",
              gap: 2,
              "& > *": { minWidth: { xs: "calc(50% - 8px)", md: "auto" } },
            }}
          >
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={80}
                  sx={{ flex: 1, borderRadius: "10px" }}
                />
              ))
            ) : (
              <>
                <KPICard
                  label="Total Órdenes"
                  value={kpis.total}
                  icon={<Receipt />}
                  darkMode={darkMode}
                />
                <KPICard
                  label="Abiertas"
                  value={kpis.abiertas}
                  icon={<TrendingUp />}
                  accent={p.accentLight}
                  darkMode={darkMode}
                />
                <KPICard
                  label="Pendientes"
                  value={kpis.pendientes}
                  icon={<Schedule />}
                  accent={p.warningBg}
                  darkMode={darkMode}
                />
                <KPICard
                  label="Finalizadas"
                  value={kpis.finalizadas}
                  icon={<CheckCircleOutline />}
                  accent={p.successBg}
                  darkMode={darkMode}
                />
              </>
            )}
          </Stack>

          {/* ── MAIN CARD ── */}
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${p.border}`,
              borderRadius: "12px",
              overflow: "hidden",
              bgcolor: p.bg,
              boxShadow: p.cardShadow,
            }}
          >
            {/* ── HEADER BAR ── */}
            <Box
              sx={{
                px: 3,
                pt: 2.5,
                pb: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    color: p.text,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Órdenes de Compra
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    color: p.textTertiary,
                    mt: 0.3,
                  }}
                >
                  {filteredItems.length} {filteredItems.length === 1 ? "orden" : "órdenes"}
                  {tabFilter !== "todas" && ` · Filtro: ${tabFilter}`}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAdd}
                disableElevation
                sx={{
                  bgcolor: p.accent,
                  color: "#FFFFFF",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.84rem",
                  fontWeight: 600,
                  borderRadius: "8px",
                  "&:hover": {
                    bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2",
                  },
                }}
              >
                Nueva Compra
              </Button>
            </Box>

            {/* ── TAB FILTERS ── */}
            <Box sx={{ px: 3, mt: 1 }}>
              <Tabs
                value={tabFilter}
                onChange={(_, v) => setTabFilter(v)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{
                  sx: {
                    bgcolor: p.accent,
                    height: 2,
                    borderRadius: 1,
                  },
                }}
                sx={{
                  minHeight: 40,
                  "& .MuiTab-root": {
                    minHeight: 40,
                    textTransform: "none",
                    fontSize: "0.82rem",
                    fontWeight: 500,
                    color: p.textTertiary,
                    px: 1.5,
                    mr: 0.5,
                    "&.Mui-selected": {
                      color: p.text,
                      fontWeight: 600,
                    },
                  },
                }}
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.key}
                    value={tab.key}
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <span>{tab.label}</span>
                        {tab.count > 0 && (
                          <Box
                            component="span"
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              bgcolor:
                                tabFilter === tab.key ? alpha(p.accent, 0.15) : p.bgTertiary,
                              color:
                                tabFilter === tab.key ? p.accent : p.textTertiary,
                              px: 0.8,
                              py: 0.15,
                              borderRadius: "5px",
                              lineHeight: 1.5,
                            }}
                          >
                            {tab.count}
                          </Box>
                        )}
                      </Stack>
                    }
                  />
                ))}
              </Tabs>
            </Box>

            {/* ── DATA GRID ── */}
            <Box sx={{ px: 1 }}>
              <DataGrid
                rows={filteredItems}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[5, 10, 20, 50]}
                checkboxSelection={false}
                disableSelectionOnClick
                autoHeight
                loading={isLoading}
                getRowId={(row) => row.Id}
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: {
                      debounceMs: 400,
                      placeholder: "Buscar por orden, proveedor...",
                      sx: {
                        "& .MuiInputBase-root": {
                          fontSize: "0.84rem",
                          borderRadius: "8px",
                          bgcolor: p.bgSecondary,
                          border: `1px solid ${p.border}`,
                          px: 1.5,
                          "&:hover": { borderColor: p.borderHover },
                          "&.Mui-focused": { borderColor: p.accent },
                          "& fieldset": { border: "none" },
                        },
                      },
                    },
                  },
                }}
                sx={{
                  border: "none",
                  fontSize: "0.84rem",
                  fontFamily: "inherit",

                  // Header
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: p.bgSecondary,
                    borderBottom: `1px solid ${p.border}`,
                    borderTop: `1px solid ${p.border}`,
                    minHeight: "44px !important",
                    maxHeight: "44px !important",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: p.textTertiary,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  },
                  "& .MuiDataGrid-columnSeparator": { display: "none" },

                  // Rows
                  "& .MuiDataGrid-row": {
                    borderBottom: `1px solid ${p.border}`,
                    transition: "background-color 0.1s ease",
                    "&:hover": { bgcolor: p.hoverBg },
                    "&:last-child": { borderBottom: "none" },
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "none",
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                    outline: "none",
                  },

                  // Toolbar
                  "& .MuiDataGrid-toolbarContainer": {
                    py: 1.5,
                    px: 2,
                    gap: 1,
                    "& .MuiButton-root": {
                      fontSize: "0.78rem",
                      color: p.textSecondary,
                      fontWeight: 500,
                      "&:hover": { bgcolor: p.hoverBg },
                    },
                  },

                  // Footer
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: `1px solid ${p.border}`,
                    minHeight: 48,
                  },
                  "& .MuiTablePagination-root": {
                    fontSize: "0.78rem",
                    color: p.textSecondary,
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      </Fade>
  );
};

export default ComprasDataTable;
